import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  useTheme,
} from '@mui/material';
import { Science as ExperimentIcon } from '@mui/icons-material';
import PaginationBar from '../../TemplateManagerDashboard/components/PaginationBar';
import {
  TemplateSelectionDialogHeader,
  TemplateGrid,
  useTemplateSelection,
} from './TemplateSelectionStepComponents';

const TemplateSelectionStep = ({
  open,
  onClose,
  onTemplateSelected,
  selectedTemplateId = null,
}) => {
  const theme = useTheme();
  const {
    searchQuery,
    categoryFilter,
    selectedTemplate,
    currentPage,
    pageSize,
    totalPages,
    templatesError,
    loading,
    loadedOnce,
    isDebouncing,
    filteredTemplates,
    categories,
    templatesWithActiveExperiments,
    apiTotalCount,
    setSearchQuery,
    setCategoryFilter,
    setSelectedTemplate,
    handlePrevPage,
    handleNextPage,
    handlePageSizeChange,
  } = useTemplateSelection({ open, selectedTemplateId });

  const handleTemplateSelect = template => {
    // Don't allow selection if template has an active experiment
    if (templatesWithActiveExperiments.has(template.id)) {
      return;
    }
    setSelectedTemplate(template);
  };

  const handleNext = () => {
    if (selectedTemplate) {
      onTemplateSelected(selectedTemplate);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setSearchQuery('');
    setCategoryFilter('all');
    handlePageSizeChange(25); // Reset to default page size
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: 'auto',
        },
      }}
      data-testid="template-selection-dialog"
    >
      <TemplateSelectionDialogHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
        categories={categories}
        isDebouncing={isDebouncing}
        onClose={handleClose}
        disableClose={loading}
      />

      <DialogContent
        sx={{
          minHeight: 400,
          maxHeight: 'calc(90vh - 240px)',
          overflowY: 'auto',
          px: 3,
          pt: 2.5,
          pb: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {templatesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {templatesError}
          </Alert>
        )}

        <TemplateGrid
          templates={filteredTemplates}
          loading={loading}
          hasAnyTemplates={(filteredTemplates?.length || 0) > 0}
          loadedOnce={loadedOnce}
          searchQuery={searchQuery}
          categoryFilter={categoryFilter}
          selectedTemplate={selectedTemplate}
          templatesWithActiveExperiments={templatesWithActiveExperiments}
          onTemplateSelect={handleTemplateSelect}
        />

        {filteredTemplates.length > 0 && totalPages > 1 && (
          <PaginationBar
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={apiTotalCount || 0}
            pageSize={pageSize}
            onPrev={handlePrevPage}
            onNext={handleNextPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={
            !selectedTemplate ||
            (selectedTemplate &&
              templatesWithActiveExperiments.has(selectedTemplate.id))
          }
          startIcon={<ExperimentIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderRadius: 1.5,
            boxShadow:
              selectedTemplate &&
              !templatesWithActiveExperiments.has(selectedTemplate.id)
                ? '0 2px 8px rgba(25, 118, 210, 0.2)'
                : 'none',
            '&:hover': {
              boxShadow:
                selectedTemplate &&
                !templatesWithActiveExperiments.has(selectedTemplate.id)
                  ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                  : 'none',
            },
          }}
        >
          Next: Create Experiment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelectionStep;
